import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import LoadSpinner from "../../../utilities/LoadSpinner";
import RoleplayScorecard from "../create_training_content/RoleplayScorecard";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_learning_roleplay_attempt } from "../../../constants";

const style = {
  position: "absolute",
  inset: 0,
  margin: "auto",
  width: "94%",
  height: "90%",
  maxWidth: "680px",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  border: "none",
};

// Manager review of one employee's roleplay attempt: the full transcript as
// read-only chat bubbles plus the same scorecard the employee saw. Fetched on
// open via the manager path (store_team_member_id) of
// get_learning_roleplay_attempt.
function RoleplayAttemptReviewModal({ open, onClose, homeCompany, attemptUuid, storeTeamMemberId, employeeName }) {
  const [attempt, setAttempt] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);

  React.useEffect(() => {
    if (!open || !attemptUuid) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setLoadError(false);
      setAttempt(null);
      try {
        const res = await getRightworkAPI(API_get_learning_roleplay_attempt, {
          company_id: homeCompany,
          attempt_uuid: attemptUuid,
          store_team_member_id: storeTeamMemberId,
        });
        if (!cancelled) {
          setAttempt(res.data?.redux?.payload?.attempt || null);
        }
      } catch (error) {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, attemptUuid, storeTeamMemberId, homeCompany]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <div className="border-b border-gray-200 px-5 py-3.5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100">
              <ConnectWithoutContactIcon className="text-violet-600" style={{ fontSize: 18 }} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-slate-900 truncate">
                {employeeName || "Practice scenario attempt"}
              </div>
              {attempt && (
                <div className="text-xs text-slate-500">
                  Attempt {attempt.attempt_number} · {attempt.turns_used} repl
                  {attempt.turns_used === 1 ? "y" : "ies"}
                </div>
              )}
            </div>
          </div>
          <button
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <LoadSpinner />
            </div>
          ) : loadError || !attempt ? (
            <p className="text-sm text-slate-500 text-center py-10">
              This attempt couldn't be loaded. Please try again.
            </p>
          ) : (
            <>
              {/* Transcript */}
              <div className="space-y-3 mb-6">
                {(attempt.messages || []).map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div className="max-w-[85%]">
                      <div
                        className={`text-[10px] font-medium uppercase tracking-wide mb-0.5 ${
                          m.role === "user"
                            ? "text-right text-violet-500"
                            : "text-slate-400"
                        }`}
                      >
                        {m.role === "user" ? employeeName || "Employee" : "Character"}
                      </div>
                      <div
                        className={`rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${
                          m.role === "user"
                            ? "bg-violet-600 text-white rounded-br-sm"
                            : "bg-slate-100 text-slate-900 rounded-bl-sm"
                        }`}
                      >
                        {m.content}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Scorecard */}
              {attempt.scorecard ? (
                <>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    AI coaching feedback
                  </div>
                  <RoleplayScorecard scorecard={attempt.scorecard} />
                </>
              ) : (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-center text-sm text-slate-500">
                  No coaching feedback was generated for this attempt.
                </div>
              )}
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
}

export default RoleplayAttemptReviewModal;
